import React, { useRef, useState, useCallback } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";

/**
 * BlogCoverCropModal
 * Shows a modal with a Cropper component for the user to select the area of the image
 * they want to use as the blog cover photo.
 *
 * Props:
 *   show        - boolean, controls modal visibility
 *   imageSrc    - string, base64 data URL of the selected image
 *   onCropDone  - function(croppedBlob), called with the cropped image Blob when user confirms
 *   onCancel    - function, called when user cancels cropping
 *   uploading   - boolean, true while the cropped image is being uploaded
 */
export default function BlogCoverCropModal({
  show,
  imageSrc,
  onCropDone,
  onCancel,
  uploading,
}) {
  const cropperRef = useRef(null);
  const [croppedPreview, setCroppedPreview] = useState(null);

  const handleCrop = useCallback(() => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      setCroppedPreview(cropper.getCroppedCanvas().toDataURL());
    }
  }, []);

  const handleConfirm = useCallback(async () => {
    const cropper = cropperRef.current?.cropper;
    if (!cropper) return;

    const canvas = cropper.getCroppedCanvas({
      maxWidth: 1920,
      maxHeight: 1080,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: "high",
    });

    canvas.toBlob(
      (blob) => {
        if (blob) {
          onCropDone(blob);
        }
      },
      "image/jpeg",
      0.92
    );
  }, [onCropDone]);

  const handleClose = useCallback(() => {
    setCroppedPreview(null);
    onCancel();
  }, [onCancel]);

  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      centered
      backdrop="static"
      className="blog-crop-modal"
    >
      <Modal.Header closeButton={!uploading}>
        <Modal.Title>Crop Cover Image</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: "0.9rem" }}>
          Drag to reposition and use the handles to resize the crop area. The
          selected area will be used as your blog cover photo.
        </p>
        {imageSrc && (
          <div className="blog-crop-container">
            <Cropper
              src={imageSrc}
              style={{ height: 400, width: "100%" }}
              aspectRatio={16 / 9}
              guides={true}
              viewMode={1}
              minCropBoxHeight={100}
              minCropBoxWidth={200}
              background={true}
              responsive={true}
              autoCropArea={1}
              checkOrientation={false}
              crop={handleCrop}
              ref={cropperRef}
            />
          </div>
        )}
        {croppedPreview && (
          <div className="blog-crop-preview-section mt-3">
            <small className="text-muted d-block mb-2">Preview:</small>
            <div className="blog-crop-preview-wrapper">
              <img src={croppedPreview} alt="Crop preview" />
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleConfirm}
          disabled={uploading}
          className="d-flex align-items-center gap-2"
        >
          {uploading ? (
            <>
              <Spinner animation="border" size="sm" />
              <span>Uploading...</span>
            </>
          ) : (
            <span>Crop & Upload</span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
